import store from '@/store';
import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { BookingDetail, ValidPass, VoucherRequest } from '@/models';
import voucherClient from '@/api/voucherClient';
import { appModule } from '@/store/modules/moduleApp';

@Module({
  dynamic: true,
  store,
  name: 'rdm-gc-booking-detail',
  namespaced: true,
})
class ModuleBookingDetail extends VuexModule {
  private bookings = [] as Array<BookingDetail>;
  private suppliers = [] as Array<any>;
  private selectedSupplier = {} as any;
  private products = [] as Array<any>;
  private selectedProduct = {} as any;
  private selectedBooking = {} as BookingDetail;
  private validPassCodes = [] as Array<ValidPass>;
  private voucherPdfs = new Map<string, string>();
  private loadingPDF = false;
  private loadingEmail = false;
  private loading = false;
  private sentConfirm = false;

  get Bookings() {
    return this.bookings;
  }

  get Suppliers() {
    return this.suppliers;
  }

  get SelectedSupplier() {
    return this.selectedSupplier;
  }

  get Products() {
    return this.products;
  }

  get SelectedProduct() {
    return this.selectedProduct;
  }

  get SelectedBooking() {
    return this.selectedBooking;
  }

  get ValidPassCodes() {
    return this.validPassCodes;
  }

  get Loading() {
    return this.loading;
  }

  get LoadingPDF(): boolean {
    return this.loadingPDF;
  }

  get LoadingEmail(): boolean {
    return this.loadingEmail;
  }

  get SentConfirm(): boolean {
    return this.sentConfirm;
  }

  get PassVouchers(): Map<string, string> {
    return this.voucherPdfs;
  }

  @Mutation
  setLoadingPDF(loadingPDF: boolean) {
    this.loadingPDF = loadingPDF;
  }

  @Mutation
  setLoadingEmail(loading: boolean) {
    this.loadingEmail = loading;
  }

  @Mutation
  setLoading(loading: boolean) {
    this.loading = loading;
  }

  @Mutation
  setVoucherPdf(_: { passID: string; data: string }) {
    const pdf = _.data != '' ? 'data:application/pdf;base64,' + _.data : '';
    this.voucherPdfs.set(_.passID, pdf);
  }

  @Mutation
  setValidPassCodes(passCodes: Array<ValidPass>) {
    this.validPassCodes = passCodes;
  }

  @Mutation
  setBookings(bookings: Array<BookingDetail>) {
    this.bookings = bookings;
  }

  @Mutation
  setSuppliers(suppliers: Array<any>) {
    this.suppliers = suppliers;
  }

  @Mutation
  setSelectedSupplier(supplier: any) {
    this.selectedSupplier = supplier;
  }

  @Mutation
  setProducts(products: Array<any>) {
    this.products = products;
  }

  @Mutation
  setSelectedProduct(product: any) {
    this.selectedProduct = product;
  }

  @Mutation
  setSelectedBooking(booking: BookingDetail) {
    this.selectedBooking = booking;
  }

  @Mutation
  setSentConfirm(sent: boolean) {
    this.sentConfirm = sent;
  }

  @Action
  addBooking(booking: BookingDetail) {
    this.Bookings.push(booking);
  }

  @Action
  removeBooking(booking: BookingDetail) {
    const i = this.Bookings.findIndex((b) => b.bookingId === booking.bookingId && b.passId === booking.passId);
    if (i > -1) {
      this.Bookings.splice(i, 1);
    }
  }

  @Action
  async getVoucher(req: VoucherRequest) {
    const passID = req.passId;
    await Promise.resolve(this.setLoadingPDF(true))
      .then(() => {
        const venueId = appModule.VenueId;
        return voucherClient.getVoucher(
          {
            bookingID: req.bookingId, //'bb5ee5cf-565d-4daf-929a-e61d72eed0a6',
            templateID: 'gocity_booking_confirmation',
            format: 'pdf',
          },
          venueId,
        );
      })
      .then((data) => this.setVoucherPdf({ passID, data }))
      .catch((err: any) => console.log('Error loading PDF for Pass ' + passID + '. Err: ' + JSON.stringify(err)))
      .finally(() => this.setLoadingPDF(false));
  }

  @Action
  async resendConfirmation(req: VoucherRequest) {
    await Promise.resolve(this.setLoadingEmail(true))
      .then(() => {
        const venueId = appModule.VenueId;
        return voucherClient.sendVoucher(
          {
            bookingID: req.bookingId,
            templateID: 'gocity_booking_confirmation',
            format: 'email',
          },
          venueId,
        );
      })
      .finally(() => {
        this.setSentConfirm(true);
        this.setLoadingEmail(false);
        setTimeout(() => {
          this.setSentConfirm(false);
        }, 5000);
      });
  }

  @Action
  chooseBookingByPassCode(passCode: string) {
    const i = this.bookings.findIndex((b) => b.passId === passCode);
    if (i > -1) {
      this.setSelectedBooking(this.bookings[i]);
      const iSupplier = this.Suppliers.findIndex((s) => s.id === this.bookings[i].supplierId);
      if (iSupplier > -1) {
        this.setSelectedSupplier(this.Suppliers[iSupplier]);
      }
      const iProduct = this.Products.findIndex((p) => p.id === this.bookings[i].productId);
      if (iProduct > -1) {
        this.setSelectedProduct(this.Products[iProduct]);
      }
    }
  }

  @Action
  chooseBookingByBookingId(bookingId: string) {
    const i = this.bookings.findIndex((b) => b.bookingId === bookingId);
    if (i > -1) {
      this.setSelectedBooking(this.bookings[i]);
      const iSupplier = this.Suppliers.findIndex((s) => s.id === this.bookings[i].supplierId);
      if (iSupplier > -1) {
        this.setSelectedSupplier(this.Suppliers[iSupplier]);
      }
      const iProduct = this.Products.findIndex((p) => p.id === this.bookings[i].productId);
      if (iProduct > -1) {
        this.setSelectedProduct(this.Products[iProduct]);
      }
    }
  }

  @Action
  appendToSuppliers(supplier: any) {
    this.suppliers.push(supplier);
  }

  @Action
  appendToProducts(product: any) {
    this.products.push(product);
  }

  @Action
  clearState() {
    this.setValidPassCodes([] as Array<ValidPass>);
    this.setBookings([]);
    this.setSuppliers([]);
    this.setSelectedSupplier({});
    this.setProducts([]);
    this.setSelectedProduct({});
    this.setSelectedBooking({} as BookingDetail);
    this.setLoading(false);
    this.setSentConfirm(false);
  }
}

export const bookingDetailModule = getModule(ModuleBookingDetail, store);
